import React, { useState } from "react";
import { api } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TaxSnapshotCard = () => {
  const [form, setForm] = useState({
    ytdIncome: "50000", ytdExpenses: "18000", monthsElapsed: String(new Date().getMonth() + 1),
    priorYearTotalTax: "", entityType: "sole prop", state: "MI",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const runCalculation = async () => {
    setLoading(true);
    try {
      const res = await api.post("/estimated-tax/direct", {
        ytdIncome: parseFloat(form.ytdIncome) || 0,
        ytdExpenses: parseFloat(form.ytdExpenses) || 0,
        monthsElapsed: parseInt(form.monthsElapsed) || 1,
        priorYearTotalTax: form.priorYearTotalTax ? parseFloat(form.priorYearTotalTax) : null,
        entityType: form.entityType,
        state: form.state || null,
      });
      if (res.data.success) setData(res.data.data);
    } catch {
      toast.error("Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-2xl shadow-sm" data-testid="tax-snapshot-card">
      <CardContent className="pt-6 space-y-4">
        <h2 className="text-xl font-semibold text-stone-900">Tax Snapshot</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input type="number" step="0.01" className="rounded-xl h-12" placeholder="YTD Income" value={form.ytdIncome}
            onChange={e => setForm({ ...form, ytdIncome: e.target.value })} data-testid="snapshot-income" />
          <Input type="number" step="0.01" className="rounded-xl h-12" placeholder="YTD Expenses" value={form.ytdExpenses}
            onChange={e => setForm({ ...form, ytdExpenses: e.target.value })} data-testid="snapshot-expenses" />
          <Input type="number" min="1" max="12" className="rounded-xl h-12" placeholder="Months Elapsed" value={form.monthsElapsed}
            onChange={e => setForm({ ...form, monthsElapsed: e.target.value })} data-testid="snapshot-months" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={form.entityType} onValueChange={v => setForm({ ...form, entityType: v })}>
            <SelectTrigger className="rounded-xl h-12" data-testid="snapshot-entity"><SelectValue placeholder="Entity Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sole prop">Sole Proprietor</SelectItem>
              <SelectItem value="single-member llc">Single-Member LLC</SelectItem>
              <SelectItem value="partnership">Partnership</SelectItem>
              <SelectItem value="s_corp">S-Corp</SelectItem>
              <SelectItem value="c_corp">C-Corp</SelectItem>
            </SelectContent>
          </Select>
          <Input className="rounded-xl h-12" placeholder="State (e.g. MI)" value={form.state}
            onChange={e => setForm({ ...form, state: e.target.value })} data-testid="snapshot-state" />
          <Input type="number" step="0.01" className="rounded-xl h-12" placeholder="Prior Year Tax (optional)" value={form.priorYearTotalTax}
            onChange={e => setForm({ ...form, priorYearTotalTax: e.target.value })} data-testid="snapshot-prior-tax" />
        </div>

        <Button onClick={runCalculation} disabled={loading} className="rounded-xl bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="snapshot-calculate-btn">
          {loading ? "Calculating..." : "Run Tax Snapshot"}
        </Button>

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="rounded-xl border p-4">
              <div className="text-sm text-stone-500">YTD Profit</div>
              <div className="text-2xl font-bold text-stone-900">${data.ytdProfit.toFixed(2)}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-sm text-stone-500">Projected Annual Profit</div>
              <div className="text-2xl font-bold text-stone-900">${data.projectedAnnualProfit.toFixed(2)}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-sm text-stone-500">Estimated Federal Tax</div>
              <div className="text-2xl font-bold text-stone-900">${data.estimatedFederalTax.toFixed(2)}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-sm text-stone-500">Estimated SE Tax</div>
              <div className="text-2xl font-bold text-stone-900">${data.estimatedSelfEmploymentTax.toFixed(2)}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-sm text-stone-500">Safe Harbor Target</div>
              <div className="text-2xl font-bold text-stone-900">${data.safeHarborTarget.toFixed(2)}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-sm text-stone-500">Reserve %</div>
              <div className="text-2xl font-bold text-stone-900">{(data.recommendedReservePct * 100).toFixed(0)}%</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TaxSnapshotCard;
